import React from 'react';

import sortBy from 'lodash/sortBy';

import { useClassnames } from '@lumapps/classnames';
import { Community } from '@lumapps/communities/types';
import { useDataAttributes } from '@lumapps/data-attributes';
import { DrawerChipsSection } from '@lumapps/lumx-drawer/components/DrawerChipsSection';
import { DrawerSectionProps } from '@lumapps/lumx-drawer/components/DrawerSection';
import { mdiCubeUnfolded } from '@lumapps/lumx/icons';
import { useTranslate } from '@lumapps/translations';

import { useQuerySpacesByIds } from '../../hooks/useQuerySpacesByIds';

export interface DrawerSpacesSectionProps extends DrawerSectionProps {
    /** list of space (community) ids if you do not have the spaces loaded */
    spacesIds?: string[];
    /** spaces to directly display if you already have them loaded */
    spaces?: Partial<Community>[];
    /** scope for tracking purposes */
    scope?: string;
}

const CLASSNAME = 'lumx-drawer-spaces-section';
const SCOPE = 'drawer-spaces-section';
/**
 * Displays a list of Spaces inside the context of a Drawer
 * @family Drawer
 * @param DrawerSpacesSectionProps
 * @returns DrawerSpacesSection
 */
export const DrawerSpacesSection: React.FC<DrawerSpacesSectionProps> = ({
    spaces: preloadedSpaces,
    spacesIds,
    scope = SCOPE,
    ...props
}) => {
    const { element } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(scope);
    const { translateObject } = useTranslate();

    const ids = React.useMemo(() => spacesIds ?? [], [spacesIds]);

    const { items: spaces, isLoading } = useQuerySpacesByIds(
        { ids },
        {
            // The hook already skips empty id lists; only avoid fetching when spaces are preloaded.
            enabled: !preloadedSpaces,
        },
    );

    const spacesToDisplay = preloadedSpaces || spaces || [];

    return (
        <DrawerChipsSection
            icon={mdiCubeUnfolded}
            {...props}
            isLoading={preloadedSpaces ? false : isLoading}
            items={sortBy(spacesToDisplay, (space) => translateObject(space.title)?.toLowerCase())}
            getItemId={(space) => space.id as string}
            getItemLabel={(space) => translateObject(space.title) || ''}
            chipProps={{
                className: element('space'),
            }}
            chipWrapperProps={{
                ...get({ element: 'spaces' }),
                className: element('spaces'),
            }}
            scope={scope}
        />
    );
};
